import React from "react";
import styled from "styled-components";
import { color, font, shadow, type } from "./tokens";
import { CardData } from "./data";
import AnimatedIcon from "./icons";

const Shell = styled.article<{ $bg: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: ${(p) => p.$bg};
  color: ${color.ink};
  border-radius: 18px;
  padding: 24px 24px 20px;
  box-shadow: ${shadow.card};
  width: 100%;
  min-height: 300px;
  text-align: left;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Serial = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${font.mono};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const BrandDot = styled.span<{ $c: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$c};
  flex: none;
`;

const Title = styled.h3`
  margin: 2px 0 0;
  font-family: ${font.body};
  font-size: 22px;
  font-weight: 700;
  line-height: 1.08;
  letter-spacing: -0.015em;
  text-wrap: balance;
`;

const Client = styled.p`
  margin: 4px 0 0;
  font-family: ${font.mono};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(14, 14, 12, 0.58);
`;

const Bullets = styled.ul`
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 7px;
  font-family: ${font.body};
  font-size: ${type.small};
  font-weight: 500;
  line-height: 1.45;
  color: rgba(14, 14, 12, 0.82);
  text-wrap: pretty;
`;

/* Minimal stack listing: one mono caption line above a hairline. */
const StackLine = styled.p`
  margin: 0;
  margin-top: auto;
  padding-top: 14px;
  border-top: 1px solid rgba(14, 14, 12, 0.14);
  font-family: ${font.mono};
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  line-height: 1.8;
  color: rgba(14, 14, 12, 0.62);
`;

/* Demos show in full — contained, never cropped. */
const Media = styled.div`
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.5);
  padding: 8px;

  img {
    display: block;
    width: 100%;
    max-width: 100%;
    height: 180px;
    object-fit: contain;
  }
`;

type Props = {
  card: CardData;
  media?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

const TradingCard: React.FC<Props> = ({ card, media, className, style }) => {
  const { project, serial, pastel } = card;
  return (
    <Shell $bg={pastel} className={className} style={style}>
      <TopRow>
        <Serial>
          <BrandDot $c={project.brand} aria-hidden />
          {serial}
        </Serial>
        <AnimatedIcon name={project.icon} size={19} strokeWidth={1.8} />
      </TopRow>
      <div>
        <Title>{project.title}</Title>
        <Client>{project.client}</Client>
      </div>
      {media ? <Media>{media}</Media> : null}
      <Bullets>
        {project.bullets.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </Bullets>
      <StackLine>{project.stack.join(" · ")}</StackLine>
    </Shell>
  );
};

export default TradingCard;
